import React, { useState, useEffect, useContext } from 'react'
import { Link } from 'react-router-dom'
import { App, Breadcrumb, Table, Dropdown, Tag, Alert } from 'antd'
import { useIntl } from 'react-intl'
import { EllipsisOutlined, ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons'
import { useManualQuery } from 'graphql-hooks'
import orderBy from 'lodash/orderBy'

import Api from '@vacationtracker/shared/services/api'
import { getAddonsSubscription, getTeamTree, getTeamTreeComplete } from '../../../../graphql/custom-queries'
import * as logger from '../../../../services/logger'

import { useAppSelector } from '../../../../store/hooks'
import { selectAuthCompanySlice } from '../../../../store/auth-company-slice'
import { selectFeatureFlagsSlice } from '../../../../store/feature-flags-slice'

import { notificationStore } from '../../../../context/notificationsContext/store'
import CircularProgress from '../../../../components/circular-progress'
import IntlMessages from '../../../../util/IntlMessages'
import { UserAvatar } from '@vacationtracker/shared/components/user-avatar'
import { wait } from '@vacationtracker/shared/functions/wait'
import { FrontendUrls } from '../../../../types/urls'
import { NUMBER_OF_TEAMS_LIMIT } from '@vacationtracker/shared/data/app-parameters'

import { IGetTeamTree, ITeamUsers, IGetTeamTreeData } from '../../../../types/teams'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { useShouldEnableFeatures } from '../../../../store/use-should-enable-features'
import { IGetAddonsSubscriptionData } from '../../../../types/custom-queries'
import { AddonTypeEnum } from '@vacationtracker/shared/types/addons'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import LinkButton from '../../../../components/link-button'
import FreemiumPricingTooltip from '../../../../components/freemium-pricing-tooltip'
import CoreCompleteTag from '../../../../components/core-complete-tag'

interface ISortedInfo {
  columnKey?: string
  order?: string
}

const TeamsPage = () => {
  const { formatMessage } = useIntl()
  const { modal, notification } = App.useApp()

  const { actionNotifications, setActionNotifications } = useContext(notificationStore)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { featureFlags } = useAppSelector(selectFeatureFlagsSlice)

  const [loading, setLoading] = useState<boolean>(true)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [sortedInfo, setSortedInfo] = useState<ISortedInfo | any>({})
  const [teamList, setTeamList] = useState<IGetTeamTree[]>([])
  const [correlationId, setCorrelationId] = useState('')
  const [deletedTeamId, setDeletedTeamId] = useState('')
  const [correlationExist, setCorrelationExist] = useState(false)
  const [allowUnlimitedTeams, setAllowUnlimitedTeams] = useState(false)

  const teamTreeComplete = useShouldEnableFeatures(SubscriptionPlanEnum.complete)

  const [ teamTreeTypeQuery ] = useManualQuery<IGetTeamTreeData>(teamTreeComplete ? getTeamTreeComplete : getTeamTree)
  const [ addonsQuery ] = useManualQuery<IGetAddonsSubscriptionData>(getAddonsSubscription)

  useEffect(() => {
    if (loading) {
      fetchTeamListAndAddons()
    }
  }, [loading])

  useEffect(() => {
    if (!correlationExist) {
      setCorrelationExist(actionNotifications.find(value => value === correlationId) ? true : false)
    }
    if (deletedTeamId.length > 0 && correlationExist && !actionNotifications.find(value => value === correlationId)) {
      setDeletedTeamId('')
      setCorrelationExist(false)
      fetchTeamListAndAddons()
    }
  }, [actionNotifications, correlationExist, correlationId, deletedTeamId, teamList])

  let numberOfRetry = 0
  const fetchTeamListAndAddons = async () => {
    const response = await teamTreeTypeQuery()
    if (!response.data || response.error) throw response.error
    const orderedTeams = orderBy(response.data.getTeamTree, [team => team.isDefault,'name'], ['desc', 'asc'])

    if (authCompany?.plan === SubscriptionPlanEnum.core) {
      const addonsResponse = await addonsQuery()
      if (addonsResponse.data?.getAddonsSubscription) {
        setAllowUnlimitedTeams(() => {
          return addonsResponse.data?.getAddonsSubscription?.addons.find(addon => addon.type === AddonTypeEnum.NO_LIMIT_TEAMS)?.status === 'ACTIVE'
        })
      }
    }

    try {
      for (const team of orderedTeams) {
        if (team.approvers.length === 0) {
          throw new Error('Team without approvers')
        }
      }

      setTeamList(orderedTeams)
      setLoading(false)
    } catch (error) {
      if (numberOfRetry >= 5) {
        setTeamList(orderedTeams)
        setLoading(false)
        // handleErrorNotification(error, undefined, formatMessage({ id: 'app.tryLateOrContactSupport'}, { code: 'ERR_001'} ))
      } else if (![FrontendUrls.signin, FrontendUrls.signup].includes(location.pathname as FrontendUrls)) {
        numberOfRetry++
        await wait(200 * numberOfRetry)
        return await fetchTeamListAndAddons()
      }
    }
  }

  const getFewUsers = (users: ITeamUsers[], teamsId: string) => {
    return (<>
      {users.map((user: ITeamUsers, index: number) => {
        if (index < 4 && user) {
          return (
            <span className="user-avatar" key={user.id}>
              <UserAvatar id={user.id} name={user.name} avatar={user.imageUrl} isTooltip={true} isLinkToPage={true} shape="circle" />
            </span>
          )
        }
        return ''
      })}
      {users.length > 4 ? <Link className="more-users" to={`/app/settings/departments/${teamsId}`}>+{users.length-4}</Link> : ''}
    </>)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleErrorNotification = (error: any, correlationErrorId?: string, title?: string): void => {
    logger.warning('error', error)
    const description = correlationErrorId ?
      formatMessage({ id: 'teams.handleDeleteError' }, { correlationId: correlationErrorId }) :
      error.response?.data?.message ? error.response?.data?.message : error.message ? error.message : JSON.stringify(error)
    notification.error({
      message: title ? title : formatMessage({ id: 'error.somethingWentWrong' }),
      description,
      duration: 0,
    })
  }

  const deleteTeam = async (teamId) => {
    let response
    try {
      response = await Api.post('/core/event', {
        eventType: 'TEAM_DELETED',
        eventGroup: 'TEAM',
        teamId,
      })

      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: 'teams.deleteInProgress' }, { teamName: response.name }),
        icon: (<LoadingOutlined />),
        duration: 0,
      })
      setCorrelationId(response.correlationId)
      setDeletedTeamId(teamId)
      setActionNotifications([
        ...actionNotifications,
        response.correlationId,
      ])
    } catch (error) {
      handleErrorNotification(error, response?.correlationId, formatMessage({ id: 'teams.handleDeleteErrorTitle' }))
    }
  }

  const showConfirmDelete = (teamId, name) => {
    modal.confirm({
      title: formatMessage({ id: 'teams.deleteTeamTitle' }),
      icon: <ExclamationCircleOutlined />,
      content: formatMessage({ id: 'teams.deleteTeamConfirm' }, { name }),
      okText: formatMessage({ id: 'app.delete' }),
      okType: 'danger',
      maskClosable: true,
      onOk() {
        deleteTeam(teamId)
      },
    })
  }

  const handleMenuClick = (event, teamId, name) => {
    if (event.key === 'delete-department') {
      showConfirmDelete(teamId, name)
    }
  }

  const menu = (teamId, name, isDefaultTeam) => ({
    onClick: (event) => handleMenuClick(event, teamId, name),
    items: [
      {
        key: 'edit-department',
        label: <Link to={`/app/settings/departments/${teamId}/edit`}><IntlMessages id="teams.editTeam" /></Link>,
      },
      {
        key: 'view-department',
        label: <Link to={`/app/settings/departments/${teamId}`}><IntlMessages id="departments.viewDepartment" /></Link>,
      },
      !isDefaultTeam ? {
        key: 'delete-department',
        label: <IntlMessages id="app.delete" />,
      } : null,
    ],
  })

  const handleTableChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter)
  }

  const shouldDisableCreatingNewTeam = (companyFeatureFlags, companyPlan, teamCount, numberOfTeamsLimit, allowUnlimited) => {
    if (companyFeatureFlags.includes(FeatureFlagEnum.noLimitTeams)) {
      return false
    }

    // Freemium plan is not allowed to create teams
    if (companyPlan === SubscriptionPlanEnum.freemium) return true

    return companyPlan === SubscriptionPlanEnum.core && teamCount >= numberOfTeamsLimit && !allowUnlimited
  }

  const columns = [{
    title: <IntlMessages id="app.name" />,
    dataIndex: 'name',
    key: 'name',
    // eslint-disable-next-line react/display-name
    sorter: (a, b) => a.name.localeCompare(b.name),
    sortOrder: sortedInfo.columnKey === 'name' && sortedInfo.order,
    // eslint-disable-next-line react/display-name
    render: (name, row) => {
      return (<>
        <Link to={`/app/settings/departments/${row.id}`}>{name}</Link>
        {row.isDefault && <Tag
          key="default"
          color="purple"
          style={{marginLeft: 4}}
        >
          <IntlMessages id="locations.default"/>
        </Tag>}
        {row.isDefault && <p className="m-0"><IntlMessages id="teams.defaultTeam" /></p>}
      </>)
    },
  }, {
    title: <IntlMessages id="teams.users" />,
    dataIndex: 'users',
    className: 'users',
    key: 'users',
    // eslint-disable-next-line react/display-name
    render: (users, row) => {
      if(users.length > 0) {
        return getFewUsers(users, row.id)
      } else {
        return <IntlMessages id="team.hasNoUsers" />
      }
    },
  }, {
    title: <IntlMessages id="teams.approvers" />,
    dataIndex: 'approvers',
    className: 'users',
    key: 'approvers',
    // eslint-disable-next-line react/display-name
    render: (approvers, row) => {
      if (approvers.length > 0) {
        return getFewUsers(approvers, row.id)
      } else {
        return ''
      }
    },
  }, {
    title: '',
    className: 'action',
    dataIndex: 'id',
    key: 'id',
    // eslint-disable-next-line react/display-name
    render: (id, row) => (
      <Dropdown menu={menu(id, row.name, row.isDefault)} trigger={['click']} placement="bottomRight">
        <EllipsisOutlined />
      </Dropdown>
    ),
  }]

  return (
    <div className='main-content'>
      <div className="main-content-header">
        <div className="main-content-header-title">
          <span><IntlMessages id="app.departments" /></span>
        </div>
        <div className="main-content-header-breadcrumb">
          <Breadcrumb
            items={[
              {
                title: <Link to={FrontendUrls.dashboard}><IntlMessages id="sidebar.dashboard" /></Link>,
              },
              {
                title: <IntlMessages id="sidebar.section.teamApprovalFlowsLowercase" />,
              },
              {
                title: <IntlMessages id="app.departments" />,
              },
            ]}
          />
        </div>
      </div>
      <div className="main-content-body">
        <div className="teams-body">
          <div className="btn-actions">
            <FreemiumPricingTooltip
              title={<IntlMessages id="components.freemiumPricingTooltip.multipleDepartments" />}
              isFreemiumPlan={authCompany?.plan === SubscriptionPlanEnum.freemium}
              freemiumData={[
                'components.freemiumPricingTooltip.oneDepartment',
              ]}
              coreData={[
                'components.freemiumPricingTooltip.upTo10Department',
                'components.freemiumPricingTooltip.customerApprovalFlows',
              ]}
              completeData={[
                'components.freemiumPricingTooltip.unlimitedDepartments',
                'components.freemiumPricingTooltip.allCoreFeatures',
                
              ]}
              featureName="create_department"
              featureCategory="departments"
            >
              <LinkButton 
                size='large'
                to="/app/settings/departments/create" 
                disabled={shouldDisableCreatingNewTeam(featureFlags, authCompany?.plan, teamList.length, NUMBER_OF_TEAMS_LIMIT, allowUnlimitedTeams)}
              >
                <IntlMessages id="teams.createNewTeam" /> 
                {authCompany?.plan === SubscriptionPlanEnum.freemium && <CoreCompleteTag />}
              </LinkButton>
            </FreemiumPricingTooltip>
          </div>
          {teamList && teamList.length ?
            <Table
              loading={loading}
              dataSource={teamList}
              columns={columns}
              rowKey={record => record.id}
              onChange={handleTableChange}
              showSorterTooltip={false}
              pagination={false}
              expandable={{
                defaultExpandAllRows: true,
                indentSize: 20,
              }}
            /> :
            <CircularProgress />
          }
          {authCompany?.plan === SubscriptionPlanEnum.core && 
           shouldDisableCreatingNewTeam(featureFlags, authCompany?.plan, teamList.length, NUMBER_OF_TEAMS_LIMIT, allowUnlimitedTeams) &&
            <Alert
              style={{ marginTop: 20 }}
              message={
                <IntlMessages id="teams.teamsLimitReached"
                  values={{
                    limitNumber: NUMBER_OF_TEAMS_LIMIT,
                    billingPage: (...chunks) => (<Link to="/app/settings/billing">{chunks}</Link>),
                  }}
                />}
              type="warning"
            />
          }
        </div>
      </div>
    </div>
  )
}

export default TeamsPage
